import { Grid, Stack } from "@mui/material";
import {
  DATE_FORMAT,
  EMPTY_CHARACTER,
  getFormattedDateString,
} from "@pulse/shared-components";
import {
  Banner,
  DatePickerField,
  DropdownInputField,
  DropdownInputFieldProps,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import { Instance } from "mobx-state-tree";
import { observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { DatePickerModel } from "../../../models/DatePickerModel";
import { TimeOfDayModel } from "../../models/TimeOfDayModel";
import { getScheduleEmailValidationErrorMessage } from "../../utils/getScheduleEmailValidationErrorMessage";
import { EmailCycleTimeDropdownInputField } from "../inputs/EmailCycleTimeDropdownInputField";
import { ReadOnlyFieldContainer } from "../inputs/ReadOnlyFieldContainer";
import { useProjectDetailsStore } from "../../../store/Hooks";
import { Info } from "lucide-react";
import { getStartOfToday } from "../../../utils/DateUtils";

interface SingleTimeScheduleEmailFormProps {
  date: Instance<typeof DatePickerModel>;
  time: Instance<typeof TimeOfDayModel>;
  readOnly?: boolean;
  scheduleMode?: "IMMEDIATE" | "LATER";
  onScheduleModeChange?: (scheduleMode: "IMMEDIATE" | "LATER") => void;
}

export const SingleTimeScheduleEmailForm: React.FC<SingleTimeScheduleEmailFormProps> =
  observer(
    ({ date, time, readOnly = false, scheduleMode, onScheduleModeChange }) => {
      const { t } = useTranslation();
      const spacing = useSpacing();
      const projectDetails = useProjectDetailsStore();
      const showScheduleModeSelector =
        !readOnly &&
        scheduleMode !== undefined &&
        onScheduleModeChange !== undefined;
      const isImmediateSchedule = scheduleMode === "IMMEDIATE";

      const currentDate = getStartOfToday();
      const disableSingleScheduleDatePicker =
        !!projectDetails.projectEndDate &&
        projectDetails.projectEndDate < currentDate;
      const validateErrors = disableSingleScheduleDatePicker
        ? undefined
        : getScheduleEmailValidationErrorMessage(t, {
            date: date.validationError,
          });
      const scheduleModeOptions = React.useMemo<
        DropdownInputFieldProps["options"]
      >(
        () => [
          {
            name: t("emailCycles.immediately"),
            value: "IMMEDIATE",
          },
          {
            name: t("emailCycles.later"),
            value: "LATER",
          },
        ],
        [t],
      );

      return (
        <Stack gap={spacing.spaceMD}>
          {showScheduleModeSelector && (
            <DropdownInputField
              name="emailCycleScheduleModeDropdown"
              label={t("emailCycles.schedule")}
              value={scheduleMode}
              options={scheduleModeOptions}
              onSelect={({ value }) =>
                onScheduleModeChange?.(value as "IMMEDIATE" | "LATER")
              }
              isRequired
            />
          )}

          {!isImmediateSchedule && (
            <Grid container columns={2} spacing={spacing.spaceMD}>
              {/* Date picker field */}
              <Grid size={1}>
                {readOnly ? (
                  <ReadOnlyFieldContainer label={t("emailCycles.date")}>
                    {date.date
                      ? getFormattedDateString(date.date)
                      : EMPTY_CHARACTER}
                  </ReadOnlyFieldContainer>
                ) : (
                  <DatePickerField
                    label={t("emailCycles.date")}
                    value={date.date}
                    onChange={date.setDate}
                    onError={date.setError}
                    format={DATE_FORMAT}
                    minDate={currentDate}
                    maxDate={projectDetails.projectEndDate ?? undefined}
                    placeholder={t("emailCycles.datePlaceholder")}
                    error={!!validateErrors?.date}
                    helperText={validateErrors?.date}
                    disabled={
                      !!projectDetails.projectEndDate &&
                      projectDetails.projectEndDate < currentDate
                    }
                    required
                  />
                )}
              </Grid>

              {/* Time picker field */}
              <Grid size={1}>
                <EmailCycleTimeDropdownInputField
                  date={date.date}
                  time={time}
                  readOnly={readOnly}
                  isRequired
                />
              </Grid>
            </Grid>
          )}

          {disableSingleScheduleDatePicker && (
            <Banner
              icon={<Info />}
              message={t("emailCycles.scheduleDatePickerDisableReasonInfo")}
              severity="info"
              width="100%"
            />
          )}
        </Stack>
      );
    },
  );
